import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchFoodStoreDataQualityReviewHistory,
  fetchFoodStoreDataQualityReviewItem,
  fetchFoodStoreDataQualityReviewQueue,
  fetchFoodStoreDataQualitySummary,
  fetchFoodStoreDataQualityTriageProgress,
  postFoodStoreDataQualityApprove,
  postFoodStoreDataQualityAssignBrand,
  postFoodStoreDataQualityBulkAction,
  postFoodStoreDataQualityCorrectGeometry,
  postFoodStoreDataQualityMarkDuplicate,
  postFoodStoreDataQualityMarkInactive,
  postFoodStoreDataQualityReject,
} from "../../../../api/foodApi";
import AdminTableContainer from "../layout/AdminTableContainer";
import FoodAccessConfidenceBadge from "../../food/FoodAccessConfidenceBadge";

const ISSUE_FILTERS = [
  { value: "", label: "All issue types" },
  { value: "UNKNOWN_BRAND", label: "Unknown brand" },
  { value: "POSSIBLE_MAJOR_CHAIN", label: "Possible major chain" },
  { value: "DUPLICATE_CANDIDATE", label: "Duplicate candidate" },
  { value: "OSM_CHAIN_CONFLICT", label: "OSM / chain conflict" },
  { value: "LOW_CONFIDENCE_GEOCODE", label: "Low-confidence geocode" },
  { value: "MISSING_GEOMETRY", label: "Missing geometry" },
  { value: "OUTSIDE_EXPECTED_AREA", label: "Outside expected area" },
  { value: "NEEDS_MANUAL_REVIEW", label: "Needs manual review" },
];

const SEVERITY_FILTERS = [
  { value: "", label: "All severities" },
  { value: "BLOCKING", label: "Blocking" },
  { value: "HIGH", label: "High" },
  { value: "MEDIUM", label: "Medium" },
  { value: "LOW", label: "Low" },
];

const BRAND_OPTIONS = ["Éxito", "Carulla", "Jumbo", "Olímpica", "D1", "Ara", "Other", "Unknown"];

const FoodStoreReviewQueuePage = ({ onClose }) => {
  const [summaryData, setSummaryData] = useState(null);
  const [triageProgress, setTriageProgress] = useState(null);
  const [queue, setQueue] = useState([]);
  const [total, setTotal] = useState(0);
  const [issueType, setIssueType] = useState("");
  const [severity, setSeverity] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [duplicateCandidatesOnly, setDuplicateCandidatesOnly] = useState(false);
  const [conflictsOnly, setConflictsOnly] = useState(false);
  const [lowConfidenceGeocodesOnly, setLowConfidenceGeocodesOnly] = useState(false);
  const [missingBrandOnly, setMissingBrandOnly] = useState(false);
  const [unknownBrandOnly, setUnknownBrandOnly] = useState(false);
  const [missingGeometryOnly, setMissingGeometryOnly] = useState(false);
  const [outOfBoundsOnly, setOutOfBoundsOnly] = useState(false);
  const [affectsMapVisibility, setAffectsMapVisibility] = useState(false);
  const [affectsAccessScoring, setAffectsAccessScoring] = useState(false);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [impactWarnings, setImpactWarnings] = useState([]);
  const [requiresAccessRecalculation, setRequiresAccessRecalculation] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState([]);
  const [reviewNotes, setReviewNotes] = useState("");
  const [assignBrand, setAssignBrand] = useState("");
  const [duplicateOfStoreId, setDuplicateOfStoreId] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [applySuggestedTypeFlags, setApplySuggestedTypeFlags] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkRunning, setBulkRunning] = useState(false);

  const loadQueue = useCallback(async () => {
    const queueData = await fetchFoodStoreDataQualityReviewQueue({
      issueType: issueType || undefined,
      severity: severity || undefined,
      brand: brandFilter || undefined,
      duplicateCandidatesOnly,
      conflictsOnly,
      lowConfidenceGeocodesOnly,
      missingBrandOnly,
      unknownBrandOnly,
      missingGeometryOnly,
      outOfBoundsOnly,
      affectsMapVisibility: affectsMapVisibility || undefined,
      affectsAccessScoring: affectsAccessScoring || undefined,
      limit: 100,
    });
    setQueue(queueData.items || []);
    setTotal(queueData.total || 0);
  }, [
    issueType,
    severity,
    brandFilter,
    duplicateCandidatesOnly,
    conflictsOnly,
    lowConfidenceGeocodesOnly,
    missingBrandOnly,
    unknownBrandOnly,
    missingGeometryOnly,
    outOfBoundsOnly,
    affectsMapVisibility,
    affectsAccessScoring,
  ]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const [summary, queueData, progress] = await Promise.all([
        fetchFoodStoreDataQualitySummary(),
        fetchFoodStoreDataQualityReviewQueue({ limit: 100 }),
        fetchFoodStoreDataQualityTriageProgress(),
      ]);
      setSummaryData(summary);
      setTriageProgress(progress);
      setQueue(queueData.items || []);
      setTotal(queueData.total || 0);
    } catch (error) {
      setQueue([]);
      setErrorMessage(error.message || "Unable to load supermarket data quality review queue.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (loading) return undefined;
    const timer = setTimeout(() => {
      loadQueue().catch((error) => {
        setErrorMessage(error.message || "Unable to refresh review queue.");
      });
    }, 200);
    return () => clearTimeout(timer);
  }, [loadQueue, loading]);

  const selectedRow = useMemo(
    () => queue.find((row) => row.id === selectedId) || detail,
    [queue, selectedId, detail]
  );

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setHistory([]);
      setImpactWarnings([]);
      setRequiresAccessRecalculation(false);
      return undefined;
    }

    let cancelled = false;
    Promise.all([
      fetchFoodStoreDataQualityReviewItem(selectedId),
      fetchFoodStoreDataQualityReviewHistory(selectedId),
    ])
      .then(([item, historyPayload]) => {
        if (cancelled) return;
        setDetail(item);
        setHistory(historyPayload.history || []);
        setAssignBrand(item.suggestedBrand || item.storeBrand || "");
        setLatitude(item.latitude != null ? String(item.latitude) : "");
        setLongitude(item.longitude != null ? String(item.longitude) : "");
        setReviewNotes("");
        setDuplicateOfStoreId("");
        setApplySuggestedTypeFlags(false);
      })
      .catch((error) => {
        if (!cancelled) {
          setErrorMessage(error.message || "Unable to load review detail.");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const runAction = async (action) => {
    if (!selectedRow) return;
    try {
      setActing(true);
      setErrorMessage("");
      setImpactWarnings([]);
      setRequiresAccessRecalculation(false);

      let result;
      if (action === "approve") {
        result = await postFoodStoreDataQualityApprove(selectedRow.id, {
          brand: assignBrand || selectedRow.storeBrand,
          reviewNotes,
        });
      } else if (action === "reject") {
        if (!window.confirm("Reject this store? It will be excluded from the public map and access scoring.")) {
          return;
        }
        result = await postFoodStoreDataQualityReject(selectedRow.id, { reviewNotes });
      } else if (action === "duplicate") {
        if (!duplicateOfStoreId) {
          setErrorMessage("Enter the canonical store ID to mark as duplicate.");
          return;
        }
        result = await postFoodStoreDataQualityMarkDuplicate(selectedRow.id, {
          duplicateOfStoreId,
          reviewNotes,
        });
      } else if (action === "brand") {
        result = await postFoodStoreDataQualityAssignBrand(selectedRow.id, {
          brand: assignBrand,
          applySuggestedTypeFlags,
          reviewNotes,
        });
      } else if (action === "geometry") {
        result = await postFoodStoreDataQualityCorrectGeometry(selectedRow.id, {
          latitude: Number(latitude),
          longitude: Number(longitude),
          notes: reviewNotes,
        });
      } else if (action === "inactive") {
        result = await postFoodStoreDataQualityMarkInactive(selectedRow.id, { reviewNotes });
      }

      if (result?.impactWarnings?.length) {
        setImpactWarnings(result.impactWarnings);
      }
      if (result?.requiresAccessRecalculation) {
        setRequiresAccessRecalculation(true);
      }

      await Promise.all([loadData(), loadQueue()]);
      if (selectedId) {
        const [item, historyPayload] = await Promise.all([
          fetchFoodStoreDataQualityReviewItem(selectedId),
          fetchFoodStoreDataQualityReviewHistory(selectedId),
        ]);
        setDetail(item);
        setHistory(historyPayload.history || []);
      }
    } catch (error) {
      setErrorMessage(error.message || "Review action failed.");
    } finally {
      setActing(false);
    }
  };

  const runBulkAction = async (action, { apply = false } = {}) => {
    const label =
      action === "BULK_APPROVE_VALID_OTHER"
        ? "approve valid Other supermarkets"
        : action === "BULK_MARK_EXACT_DUPLICATES"
          ? "mark exact duplicate candidates"
          : "defer low-priority records";
    const mode = apply ? "apply" : "dry run";
    if (
      apply &&
      !window.confirm(
        `Run bulk ${label} (${mode})? Ambiguous or major-chain records are excluded by policy.`
      )
    ) {
      return;
    }
    try {
      setBulkRunning(true);
      setErrorMessage("");
      const result = await postFoodStoreDataQualityBulkAction({
        action,
        sourceKey: "OSM_FOOD_POIS",
        dryRun: !apply,
        apply,
        limit: 100,
        distanceMeters: 50,
      });
      setBulkResult(result);
      if (apply) {
        await loadData();
        await loadQueue();
      }
    } catch (error) {
      setErrorMessage(error.message || "Bulk action failed.");
    } finally {
      setBulkRunning(false);
    }
  };

  const summary = summaryData?.summary || {};

  return (
    <div className="admin-page food-store-review-page">
      <header className="admin-page__header">
        <div>
          <p className="admin-page__eyebrow">Food Data</p>
          <h1>Supermarket Data Quality</h1>
          <p className="muted">
            Review unmatched, duplicate, suspicious, and low-confidence supermarket records before they affect the Food Access Map.
          </p>
        </div>
        {onClose ? (
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Close
          </button>
        ) : null}
      </header>

      {triageProgress ? (
        <section className="panel-card food-data-quality-summary">
          <h2>Triage progress</h2>
          <div className="food-data-quality-summary__grid">
            <div>Open review items: {triageProgress.openReviewItems ?? 0}</div>
            <div>Resolved today: {triageProgress.resolvedToday ?? 0}</div>
            <div>Blocking remaining: {triageProgress.remainingBlocking ?? 0}</div>
            <div>Duplicate candidates: {triageProgress.duplicateCandidatesRemaining ?? 0}</div>
            <div>Unknown brands: {triageProgress.unknownBrandRemaining ?? 0}</div>
            <div>Low-confidence geocodes: {triageProgress.lowConfidenceGeocodesRemaining ?? 0}</div>
            <div>Map excluded: {triageProgress.publicMapExcluded ?? 0}</div>
            <div>Access excluded: {triageProgress.accessScoringExcluded ?? 0}</div>
          </div>
          <p className="muted">{triageProgress.nextRecommendedAction}</p>
          {triageProgress.requiresAccessRecalculation ? (
            <p className="food-comparison-setup" role="status">
              Access area recalculation will be required after map-impacting review decisions.
            </p>
          ) : null}
        </section>
      ) : null}

      {summaryData ? (
        <section className="panel-card food-data-quality-summary">
          <h2>Summary</h2>
          <div className="food-data-quality-summary__grid">
            <div>Open issues: {summary.openReviewItems ?? 0}</div>
            <div>Blocking: {summary.blockingIssues ?? 0}</div>
            <div>Duplicate candidates: {summary.duplicateCandidates ?? 0}</div>
            <div>Low-confidence geocodes: {summary.lowConfidenceGeocodes ?? 0}</div>
            <div>Unknown brand: {summary.unknownBrandStores ?? 0}</div>
            <div>Possible major chain: {summary.possibleMajorChainStores ?? 0}</div>
            <div>Unresolved conflicts: {summary.unresolvedConflicts ?? 0}</div>
          </div>
        </section>
      ) : null}

      <section className="panel-card">
        <h2>Safe bulk actions</h2>
        <p className="muted">
          Dry run first. Bulk approve excludes possible major chains, duplicate candidates, and low-confidence geocodes.
        </p>
        <div className="food-normalization-page__control-row">
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            disabled={bulkRunning}
            onClick={() => runBulkAction("BULK_APPROVE_VALID_OTHER")}
          >
            Dry run: approve valid Other
          </button>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            disabled={bulkRunning}
            onClick={() => runBulkAction("BULK_APPROVE_VALID_OTHER", { apply: true })}
          >
            Apply: approve valid Other
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            disabled={bulkRunning}
            onClick={() => runBulkAction("BULK_MARK_EXACT_DUPLICATES")}
          >
            Dry run: mark exact duplicates
          </button>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            disabled={bulkRunning}
            onClick={() => runBulkAction("BULK_MARK_EXACT_DUPLICATES", { apply: true })}
          >
            Apply: mark exact duplicates
          </button>
        </div>
        {bulkResult ? (
          <p className="muted">
            {bulkResult.action}: {bulkResult.dryRun ? "dry run" : "applied"} · eligible{" "}
            {bulkResult.eligible ?? bulkResult.candidateGroups ?? "—"} · processed{" "}
            {bulkResult.appliedCount ?? 0}
          </p>
        ) : null}
      </section>

      <section className="panel-card food-normalization-page__control-row">
        <label>
          Issue type
          <select value={issueType} onChange={(e) => setIssueType(e.target.value)}>
            {ISSUE_FILTERS.map((option) => (
              <option key={option.value || "all"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Severity
          <select value={severity} onChange={(e) => setSeverity(e.target.value)}>
            {SEVERITY_FILTERS.map((option) => (
              <option key={option.value || "all"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Brand
          <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)}>
            <option value="">All brands</option>
            {BRAND_OPTIONS.map((brand) => (
              <option key={brand} value={brand}>
                {brand}
              </option>
            ))}
          </select>
        </label>
        <label className="food-comparison-filters__checkbox">
          <input
            type="checkbox"
            checked={duplicateCandidatesOnly}
            onChange={(e) => setDuplicateCandidatesOnly(e.target.checked)}
          />
          Duplicate candidates
        </label>
        <label className="food-comparison-filters__checkbox">
          <input
            type="checkbox"
            checked={conflictsOnly}
            onChange={(e) => setConflictsOnly(e.target.checked)}
          />
          Conflicts only
        </label>
        <label className="food-comparison-filters__checkbox">
          <input
            type="checkbox"
            checked={lowConfidenceGeocodesOnly}
            onChange={(e) => setLowConfidenceGeocodesOnly(e.target.checked)}
          />
          Low-confidence geocodes
        </label>
        <label className="food-comparison-filters__checkbox">
          <input
            type="checkbox"
            checked={missingBrandOnly}
            onChange={(e) => setMissingBrandOnly(e.target.checked)}
          />
          Missing brand
        </label>
        <label className="food-comparison-filters__checkbox">
          <input
            type="checkbox"
            checked={unknownBrandOnly}
            onChange={(e) => setUnknownBrandOnly(e.target.checked)}
          />
          Unknown brand only
        </label>
        <label className="food-comparison-filters__checkbox">
          <input
            type="checkbox"
            checked={missingGeometryOnly}
            onChange={(e) => setMissingGeometryOnly(e.target.checked)}
          />
          Missing geometry
        </label>
        <label className="food-comparison-filters__checkbox">
          <input
            type="checkbox"
            checked={outOfBoundsOnly}
            onChange={(e) => setOutOfBoundsOnly(e.target.checked)}
          />
          Out of bounds
        </label>
        <label className="food-comparison-filters__checkbox">
          <input
            type="checkbox"
            checked={affectsMapVisibility}
            onChange={(e) => setAffectsMapVisibility(e.target.checked)}
          />
          Affects map
        </label>
        <label className="food-comparison-filters__checkbox">
          <input
            type="checkbox"
            checked={affectsAccessScoring}
            onChange={(e) => setAffectsAccessScoring(e.target.checked)}
          />
          Affects access scoring
        </label>
        <button type="button" className="btn btn--ghost" onClick={loadData} disabled={loading}>
          Refresh
        </button>
      </section>

      {errorMessage ? <p className="food-dashboard__error">{errorMessage}</p> : null}
      {impactWarnings.length ? (
        <section className="panel-card">
          <h3>Decision impact</h3>
          <ul>
            {impactWarnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
          {requiresAccessRecalculation ? (
            <p className="muted">Access area recalculation is recommended after this decision.</p>
          ) : null}
        </section>
      ) : null}

      <AdminTableContainer title={`Review queue (${total})`} loading={loading}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Store</th>
              <th>Brand</th>
              <th>Issue</th>
              <th>Severity</th>
              <th>Source</th>
              <th>Municipality</th>
              <th>Review status</th>
              <th>Confidence</th>
              <th>Access impact</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {queue.map((row) => (
              <tr
                key={row.id}
                className={selectedId === row.id ? "admin-table__row--selected" : ""}
                onClick={() => setSelectedId(row.id)}
              >
                <td>{row.storeName || "—"}</td>
                <td>{row.storeBrand || "—"}</td>
                <td>{row.issueLabel || row.issueType || "—"}</td>
                <td>{row.severity || "—"}</td>
                <td>{row.sourceKey || "—"}</td>
                <td>{row.municipality || row.city || "—"}</td>
                <td>{row.reviewStatus}</td>
                <td>
                  <FoodAccessConfidenceBadge
                    score={row.confidenceScore}
                    label={row.confidenceLabel}
                    publicLabel={row.publicConfidenceLabel}
                    warnings={row.confidenceWarnings}
                    showTooltip
                    size="sm"
                  />
                </td>
                <td>{row.accessScoringImpact || "—"}</td>
                <td>{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </AdminTableContainer>

      {selectedRow ? (
        <section className="panel-card">
          <h2>Review selected store</h2>
          <p>
            <strong>{selectedRow.storeName || "Unnamed store"}</strong> · {selectedRow.storeBrand || "Unknown"} ·{" "}
            {selectedRow.latitude ?? "—"}, {selectedRow.longitude ?? "—"}
          </p>
          <p className="muted">
            Source: {selectedRow.sourceKey || "—"} · Recommended action: {selectedRow.recommendedAction || selectedRow.defaultRecommendedAction || "—"}
          </p>
          <p className="muted">
            Map eligible: {selectedRow.mapEligible ? "Yes" : "No"} · Access eligible:{" "}
            {selectedRow.accessEligible ? "Yes" : "No"}
          </p>
          {selectedRow.sourceQualityNotes ? (
            <p className="muted">Source notes: {selectedRow.sourceQualityNotes}</p>
          ) : null}
          {selectedRow.duplicateCandidateCount > 0 ? (
            <p className="muted">Duplicate candidates nearby/grouped: {selectedRow.duplicateCandidateCount}</p>
          ) : null}
          {detail?.duplicateReviewContext?.candidates?.length ? (
            <div>
              <h3>Duplicate review</h3>
              <p className="muted">
                Recommended primary: {detail.duplicateReviewContext.recommendedPrimaryStoreId}
                {detail.duplicateReviewContext.recommendedReason
                  ? ` · ${detail.duplicateReviewContext.recommendedReason}`
                  : ""}
              </p>
              <ul>
                {detail.duplicateReviewContext.candidates.map((candidate) => (
                  <li key={candidate.storeId}>
                    {candidate.storeName} ({candidate.storeBrand}) · {candidate.distanceMeters} m ·{" "}
                    {candidate.sourceStoreId || candidate.storeId}
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => setDuplicateOfStoreId(candidate.storeId)}
                    >
                      Use as canonical
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {detail?.suggestedBrand && detail.suggestedBrand !== detail.storeBrand ? (
            <p className="muted">Suggested brand from OSM tags/name: {detail.suggestedBrand}</p>
          ) : null}
          {detail?.osmTags ? (
            <p className="muted">
              OSM tags: brand={detail.osmTags.brand || "—"} · name={detail.osmTags.name || "—"} · shop=
              {detail.osmTags.shop || "—"}
            </p>
          ) : null}

          <div className="food-normalization-page__control-row">
            <label>
              Assign brand
              <select value={assignBrand} onChange={(e) => setAssignBrand(e.target.value)}>
                {BRAND_OPTIONS.map((brand) => (
                  <option key={brand} value={brand}>
                    {brand}
                  </option>
                ))}
              </select>
            </label>
            <label className="food-comparison-filters__checkbox">
              <input
                type="checkbox"
                checked={applySuggestedTypeFlags}
                onChange={(e) => setApplySuggestedTypeFlags(e.target.checked)}
              />
              Apply suggested store type flags
            </label>
            <label>
              Duplicate of store ID
              <input
                value={duplicateOfStoreId}
                onChange={(e) => setDuplicateOfStoreId(e.target.value)}
                placeholder="UUID of canonical store"
              />
            </label>
            <label>
              Latitude
              <input value={latitude} onChange={(e) => setLatitude(e.target.value)} />
            </label>
            <label>
              Longitude
              <input value={longitude} onChange={(e) => setLongitude(e.target.value)} />
            </label>
            <label>
              Review notes
              <input value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} />
            </label>
          </div>

          <div className="food-normalization-page__control-row">
            <button type="button" className="btn btn--primary" disabled={acting} onClick={() => runAction("approve")}>
              Approve
            </button>
            <button type="button" className="btn btn--ghost" disabled={acting} onClick={() => runAction("brand")}>
              Assign brand
            </button>
            <button type="button" className="btn btn--ghost" disabled={acting} onClick={() => runAction("duplicate")}>
              Mark duplicate
            </button>
            <button type="button" className="btn btn--ghost" disabled={acting} onClick={() => runAction("geometry")}>
              Correct geometry
            </button>
            <button type="button" className="btn btn--ghost" disabled={acting} onClick={() => runAction("inactive")}>
              Mark inactive
            </button>
            <button type="button" className="btn btn--danger" disabled={acting} onClick={() => runAction("reject")}>
              Reject
            </button>
            <a
              className="btn btn--ghost"
              href={`#/food/food-access?lat=${selectedRow.latitude ?? ""}&lng=${selectedRow.longitude ?? ""}`}
            >
              Open in Food Access Map
            </a>
          </div>

          {history.length ? (
            <div>
              <h3>Decision history</h3>
              <ul>
                {history.map((entry) => (
                  <li key={entry.id}>
                    {entry.decision} · {entry.reviewedBy || "admin"} ·{" "}
                    {entry.reviewedAt ? new Date(entry.reviewedAt).toLocaleString() : "—"}
                    {entry.notes ? ` · ${entry.notes}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
};

export default FoodStoreReviewQueuePage;
